import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

const API = process.env.REACT_APP_API_URL;

/**
 * The Master FDR Matrix as a live dashboard — FULL parity with the Excel
 * export (one shared server loader, so they can never drift): one row per
 * vendor with an X-column per delegated function (scope on hover), the
 * service description, contract dates, accreditation, and the FDR contacts,
 * exactly the workbook's columns. Related entities below, like the second
 * sheet.
 *
 * Two ways to ask, both grounded ONLY in the register record with the exact
 * refusal sentence when the record is silent:
 *   - "Ask a column": one question answered per vendor, rendered as a NEW
 *     matrix column (e.g. "is the UM vendor also delegated for denials?").
 *   - Per-vendor questions inside the row expansion.
 */

const fmtDate = (v) => {
  if (!v) return '—';
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? new Date(`${s}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    : s;
};

function contractHealth(v, now = new Date()) {
  if (!v.term && !v.effective) return { k: 'none', label: 'No contract dates', tone: 'bg-slate-100 text-slate-500' };
  if (!v.term) return { k: 'active', label: 'Active', tone: 'bg-emerald-100 text-emerald-700' };
  const term = new Date(`${String(v.term).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(term.getTime())) return { k: 'active', label: 'Active', tone: 'bg-emerald-100 text-emerald-700' };
  const days = Math.floor((term - now) / 86400000);
  if (days < 0) return { k: 'expired', label: 'Term passed', tone: 'bg-rose-100 text-rose-700' };
  if (days <= 90) return { k: 'expiring', label: `Term in ${days}d`, tone: 'bg-amber-100 text-amber-800' };
  return { k: 'active', label: 'Active', tone: 'bg-emerald-100 text-emerald-700' };
}

const REFUSAL = 'The register does not record that.';

/**
 * A truncated cell that expands IN PLACE on click — the full value is never
 * more than one click away, without leaving the grid. Clicks stay in the
 * cell (they never toggle the row).
 */
function ExpandableText({ text, className = '' }) {
  const [full, setFull] = useState(false);
  if (!text) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <span
      onClick={(e) => { e.stopPropagation(); setFull(!full); }}
      title={full ? 'Click to collapse' : text}
      className={`cursor-pointer ${full ? 'whitespace-normal break-words' : 'truncate block'} ${className}`}
    >
      {text}
    </span>
  );
}

/** An email chip: click copies the address, with visible feedback. */
function CopyEmail({ email }) {
  const [copied, setCopied] = useState(false);
  if (!email) return null;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard unavailable — the address is still visible to select */ }
  };
  return (
    <button type="button" onClick={copy}
      title={copied ? 'Copied' : `Click to copy ${email}`}
      className={`inline-flex items-center gap-1 max-w-full px-1.5 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
        copied ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200' : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-orange-50 hover:text-orange-800 hover:ring-orange-200'}`}>
      <span className="truncate">{copied ? 'Copied' : email}</span>
    </button>
  );
}

function AskVendor({ vendor }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);

  const ask = async () => {
    const question = q.trim();
    if (!question || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/fdr/contracts/vendors/${vendor.id}/ask`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });
      const d = await r.json();
      setThread((t) => [...t, { q: question, a: d.success ? d.answer : (d.message || 'Could not answer right now.') }]);
      setQ('');
    } catch {
      setThread((t) => [...t, { q: question, a: 'Could not answer right now.' }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Ask about this vendor</div>
      <p className="text-xs text-slate-500 mt-0.5">Answers come only from this vendor's register record — each statement names the field it came from. What the register does not hold, it will say so.</p>
      <div className="mt-2 space-y-3">
        {thread.map((t, i) => (
          <div key={i} className="text-sm">
            <div className="font-medium text-slate-800">{t.q}</div>
            <div className="mt-1 text-slate-600 whitespace-pre-wrap rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">{t.a}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
          placeholder={`e.g. What functions are delegated to ${vendor.name}?`}
          className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
        />
        <button type="button" onClick={ask} disabled={busy || !q.trim()}
          className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold disabled:opacity-50 hover:shadow-lg hover:shadow-orange-500/25 transition-all">
          {busy ? 'Reading the record — a full contract read can take up to a minute…' : 'Ask'}
        </button>
      </div>
    </div>
  );
}

export default function FdrMatrixModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [fCats, setFCats] = useState(() => new Set()); // function-column filters (has X)
  const [fHealth, setFHealth] = useState('');
  const [openFilter, setOpenFilter] = useState(null);
  const [showRelated, setShowRelated] = useState(false);

  // Question columns: [{question, answers: {vendorId: text}, id?: savedId}]
  // Saved ones (id set) live on the permanent record and load with the modal.
  const [qCols, setQCols] = useState([]);
  const [askingCol, setAskingCol] = useState(false);
  const [colQuestion, setColQuestion] = useState('');
  const [colBusy, setColBusy] = useState(false);
  const [colErr, setColErr] = useState('');
  const [colProgress, setColProgress] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API}/api/fdr/contracts/matrix.json`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d.success) setData(d); })
      .finally(() => setLoading(false));
    // Saved question columns — the permanent record.
    fetch(`${API}/api/fdr/contracts/matrix/questions`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setQCols((d.questions || []).map((q) => ({ id: q.id, question: q.question, answers: q.answers || {} })));
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const now = new Date();
  const vendors = useMemo(() => {
    let list = data?.vendors || [];
    const needle = search.trim().toLowerCase();
    if (needle) list = list.filter((v) => [v.name, v.description, v.accreditations].filter(Boolean).join(' ').toLowerCase().includes(needle));
    if (fCats.size) list = list.filter((v) => [...fCats].every((c) => c in (v.functions || {})));
    if (fHealth) list = list.filter((v) => contractHealth(v, now).k === fHealth);
    return list;
  }, [data, search, fCats, fHealth]);

  if (!open) return null;

  const cats = data?.usedCategories || [];
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleCat = (key) => setFCats((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // The question runs as a background job on the server (a full-contract
  // read across every vendor outlives any single web request). We poll for
  // progress; the column lands when every contract has been read.
  const askColumn = async () => {
    const question = colQuestion.trim();
    if (!question || colBusy) return;
    setColBusy(true); setColErr(''); setColProgress('Starting…');
    try {
      const r = await fetch(`${API}/api/fdr/contracts/matrix/ask-column`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });
      const d = await r.json();
      if (!d.success || !d.jobId) { setColErr(d.message || 'Could not start the question.'); setColBusy(false); return; }

      const deadline = Date.now() + 12 * 60 * 1000;
      const poll = async () => {
        if (Date.now() > deadline) { setColErr('This is taking too long — try again.'); setColBusy(false); return; }
        try {
          const s = await fetch(`${API}/api/fdr/contracts/matrix/ask-column/${d.jobId}`, { credentials: 'include' }).then((x) => x.json());
          if (s.status === 'done') {
            setQCols((cols) => [...cols, { question: s.question, answers: s.answers }]);
            setColQuestion('');
            setAskingCol(false);
            setColBusy(false);
            setColProgress('');
            return;
          }
          if (s.status === 'error' || !s.success) { setColErr(s.message || 'Could not answer right now.'); setColBusy(false); return; }
          const p = s.progress || {};
          setColProgress(p.current
            ? `${p.current}${p.total ? ` (${Math.min(p.done + 1, p.total)} of ${p.total})` : ''}`
            : 'Working…');
        } catch { /* transient poll miss — keep going */ }
        setTimeout(poll, 2500);
      };
      poll();
    } catch {
      setColErr('Could not answer right now.');
      setColBusy(false);
    }
  };

  // Fixed columns mirror the workbook exactly; question columns append.
  // Compact, Asana-density widths — nothing balloons; long values truncate
  // with the full text on hover and in the expansion.
  const gridCols = `13rem 13rem ${cats.map(() => '5.5rem').join(' ')} 6.5rem 6.5rem 8rem 9rem 11rem ${qCols.map(() => '13rem').join(' ')}`.trim();

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-[min(84rem,97vw)] max-h-[92vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>

        {/* Header — the app's own light theme with the orange accent. */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-white">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Master FDR Matrix</h2>
            <p className="text-xs text-slate-500 mt-0.5">Live from the register — the same columns the Excel export carries. Click a function header to filter; click a vendor for the full record.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendors…"
              className="w-52 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 focus:outline-none transition-colors"
            />
            <button type="button" onClick={() => { setAskingCol(!askingCol); setColErr(''); }}
              className="px-3 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-semibold hover:shadow-lg hover:shadow-orange-500/25 transition-all whitespace-nowrap">
              + Ask a column
            </button>
            <a href={`${API}/api/fdr/contracts/matrix.xlsx`}
              className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors whitespace-nowrap">
              Export (Excel)
            </a>
            <button type="button" onClick={onClose} className="ml-1 w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 text-lg leading-none">×</button>
          </div>
        </div>

        {/* Ask-a-column bar */}
        {askingCol && (
          <div className="px-6 py-3 border-b border-slate-200 bg-orange-50/50">
            <div className="flex items-center gap-2">
              <input
                type="text" autoFocus value={colQuestion} onChange={(e) => setColQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') askColumn(); if (e.key === 'Escape') setAskingCol(false); }}
                placeholder='e.g. If the vendor is delegated for UM, is it also delegated for denials?'
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
              />
              <button type="button" onClick={askColumn} disabled={colBusy || !colQuestion.trim()}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold disabled:opacity-50">
                {colBusy ? (colProgress || 'Working…') : 'Add column'}
              </button>
              <button type="button" onClick={() => setAskingCol(false)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 text-sm">Cancel</button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">One question, answered for every vendor from its own register record. Where the register is silent, the cell says so — nothing is inferred.</p>
            {colErr && <p className="text-xs text-rose-600 mt-1">{colErr}</p>}
          </div>
        )}

        {/* Body — the grid scrolls horizontally as one unit, like the workbook. */}
        <div className="flex-1 overflow-auto">
          {loading && <div className="p-10 text-center text-slate-500 text-sm">Reading the register…</div>}
          {!loading && data && (
            <div className="min-w-max">
              {/* Column headers */}
              <div className="grid gap-2 px-6 py-2.5 border-b border-slate-200 bg-slate-50/80 sticky top-0 z-10 items-end" style={{ gridTemplateColumns: gridCols }}>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Vendor</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Description</span>
                {cats.map((c) => (
                  <button key={c.key} type="button" onClick={() => toggleCat(c.key)}
                    title={fCats.has(c.key) ? `Filtered: delegated for ${c.label} — click to clear` : `Show only vendors delegated for ${c.label}`}
                    className={`text-[11px] font-semibold uppercase tracking-wide text-left leading-tight ${fCats.has(c.key) ? 'text-orange-700' : 'text-slate-500 hover:text-slate-800'}`}>
                    {c.label} <span className="text-[9px]">{fCats.has(c.key) ? '●' : '▾'}</span>
                  </button>
                ))}
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Effective</span>
                <div className="relative">
                  <button type="button" onClick={() => setOpenFilter(openFilter === 'health' ? null : 'health')}
                    className={`text-[11px] font-semibold uppercase tracking-wide ${fHealth ? 'text-orange-700' : 'text-slate-500 hover:text-slate-800'}`}>
                    Term <span className="text-[9px]">{fHealth ? '●' : '▾'}</span>
                  </button>
                  {openFilter === 'health' && (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setOpenFilter(null)} />
                      <div className="absolute left-0 top-full z-30 mt-1 w-48 rounded-xl border border-slate-200 bg-white shadow-lg py-1 normal-case tracking-normal">
                        {[['', 'All'], ['active', 'Active'], ['expiring', 'Term ≤ 90 days'], ['expired', 'Term passed'], ['none', 'No contract dates']].map(([v, l]) => (
                          <button key={v} type="button" onClick={() => { setFHealth(v); setOpenFilter(null); }}
                            className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${fHealth === v ? 'text-orange-700 font-semibold' : 'text-slate-700'}`}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Accreditation</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">FDR Contact</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">FDR Email</span>
                {qCols.map((qc, i) => (
                  <span key={i} className="text-[11px] font-semibold tracking-wide text-orange-700 leading-tight normal-case" title={qc.question}>
                    {qc.question.length > 60 ? `${qc.question.slice(0, 59)}…` : qc.question}
                    {qc.id
                      ? <span className="ml-1 px-1 py-px rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold align-middle" title="Saved to the permanent record">KEPT</span>
                      : (
                        <button type="button"
                          onClick={async () => {
                            try {
                              const r = await fetch(`${API}/api/fdr/contracts/matrix/questions`, {
                                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ question: qc.question, answers: qc.answers }),
                              });
                              const d = await r.json();
                              if (d.success) setQCols((cols) => cols.map((c, j) => (j === i ? { ...c, id: d.question.id } : c)));
                            } catch { /* stays unsaved */ }
                          }}
                          className="ml-1 px-1 py-px rounded bg-slate-100 text-slate-600 text-[9px] font-bold hover:bg-emerald-100 hover:text-emerald-700 align-middle"
                          title="Keep this question and its answers on the permanent record">
                          KEEP
                        </button>
                      )}
                    <button type="button"
                      onClick={async () => {
                        if (qc.id) {
                          try { await fetch(`${API}/api/fdr/contracts/matrix/questions/${qc.id}`, { method: 'DELETE', credentials: 'include' }); }
                          catch { /* the column still leaves the view */ }
                        }
                        setQCols((cols) => cols.filter((_, j) => j !== i));
                      }}
                      className="ml-1 text-slate-400 hover:text-rose-600"
                      title={qc.id ? 'Remove from the permanent record' : 'Remove this column'}>×</button>
                  </span>
                ))}
              </div>

              {/* Vendor rows */}
              <div className="divide-y divide-slate-100">
                {vendors.map((v) => {
                  const isOpen = expanded.has(v.id);
                  const health = contractHealth(v, now);
                  const fnKeys = Object.keys(v.functions || {});
                  return (
                    <div key={v.id}>
                      <div className={`grid gap-2 px-6 py-2 items-center cursor-pointer hover:bg-orange-50/40 ${isOpen ? 'bg-slate-50/70' : ''}`}
                        style={{ gridTemplateColumns: gridCols }}
                        onClick={() => toggle(v.id)}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 text-xs w-3">{isOpen ? '▾' : '▸'}</span>
                            <span className="text-sm font-semibold text-slate-800 truncate" title={v.name}>{v.name}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${health.tone}`}>{health.label}</span>
                          </div>
                        </div>
                        <ExpandableText text={v.description} className="text-xs text-slate-600" />
                        {cats.map((c) => (
                          <span key={c.key} className="text-center">
                            {c.key in (v.functions || {})
                              ? <span className="inline-flex px-2 py-0.5 rounded-md bg-orange-50 text-orange-800 ring-1 ring-orange-200 text-[11px] font-bold" title={v.functions[c.key] || c.label}>X</span>
                              : <span className="text-slate-200 text-xs">—</span>}
                          </span>
                        ))}
                        <span className="text-xs text-slate-600 tabular-nums">{fmtDate(v.effective)}</span>
                        <span className={`text-xs tabular-nums ${health.k === 'expired' ? 'text-rose-600 font-semibold' : health.k === 'expiring' ? 'text-amber-700 font-semibold' : 'text-slate-600'}`}>{fmtDate(v.term)}</span>
                        <ExpandableText text={v.accreditations} className="text-xs text-slate-600" />
                        <ExpandableText
                          text={(v.contacts || []).map((c) => [c.name, c.title].filter(Boolean).join(', ')).join('; ')}
                          className="text-xs text-slate-600"
                        />
                        <span className="flex flex-wrap gap-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                          {(v.contacts || []).map((c) => c.email).filter(Boolean).map((em, i) => <CopyEmail key={i} email={em} />)}
                          {(v.contacts || []).every((c) => !c.email) && <span className="text-slate-300 text-xs">—</span>}
                        </span>
                        {qCols.map((qc, i) => {
                          const a = qc.answers[v.id] || REFUSAL;
                          const silent = a.startsWith(REFUSAL);
                          return (
                            <ExpandableText key={i} text={a} className={`text-xs leading-snug ${silent ? 'text-slate-400' : 'text-slate-700'}`} />
                          );
                        })}
                      </div>

                      {isOpen && (
                        <div className="px-11 pb-5 pt-1 bg-slate-50/60" style={{ maxWidth: '80rem' }}>
                          {v.description && (
                            <div className="mb-3">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Service description</div>
                              <p className="text-sm text-slate-700 leading-relaxed">{v.description}</p>
                            </div>
                          )}
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="rounded-xl border border-slate-200 bg-white p-4">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Delegated functions ({fnKeys.length})</div>
                              {fnKeys.length === 0 && <p className="text-sm text-slate-400">None recorded.</p>}
                              <div className="space-y-1.5">
                                {fnKeys.map((k) => (
                                  <div key={k} className="text-sm">
                                    <span className="font-medium text-slate-800">{cats.find((c) => c.key === k)?.label || k}</span>
                                    {v.functions[k] && <span className="text-slate-500"> — {v.functions[k]}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-white p-4">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Contract &amp; contacts</div>
                              <div className="text-sm text-slate-700 space-y-1">
                                <div><span className="text-slate-400">Effective:</span> {fmtDate(v.effective)}</div>
                                <div><span className="text-slate-400">Term:</span> {fmtDate(v.term)}</div>
                                <div><span className="text-slate-400">Accreditation:</span> {v.accreditations || 'None recorded'}</div>
                              </div>
                              <div className="mt-3 space-y-1">
                                {(v.contacts || []).map((c, i) => (
                                  <div key={i} className="text-sm text-slate-700 flex items-center gap-1.5 flex-wrap">
                                    <span className="font-medium">{c.name}</span>
                                    {c.title && <span className="text-slate-500">· {c.title}</span>}
                                    {c.email && <CopyEmail email={c.email} />}
                                  </div>
                                ))}
                                {(v.contacts || []).length === 0 && <p className="text-sm text-slate-400">No contacts recorded.</p>}
                              </div>
                            </div>
                          </div>
                          <AskVendor vendor={v} />
                        </div>
                      )}
                    </div>
                  );
                })}
                {vendors.length === 0 && (
                  <div className="p-10 text-center text-slate-500 text-sm">No vendors match these filters.</div>
                )}
              </div>

              {(data.related || []).length > 0 && (
                <div className="border-t border-slate-200 px-6 py-4" style={{ maxWidth: '80rem' }}>
                  <button type="button" onClick={() => setShowRelated(!showRelated)}
                    className="text-sm font-semibold text-slate-700 hover:text-slate-900">
                    {showRelated ? '▾' : '▸'} Related entities ({data.related.length})
                  </button>
                  {showRelated && (
                    <div className="mt-3 space-y-2">
                      {data.related.map((e) => (
                        <div key={e.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                          <div className="text-sm font-semibold text-slate-800">{e.name}</div>
                          {e.description && <div className="text-xs text-slate-500 mt-0.5">{e.description}</div>}
                          {e.notes && <div className="text-xs text-slate-600 mt-1"><span className="text-slate-400">Oversight:</span> {e.notes}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between">
          <span className="text-xs text-slate-500">{vendors.length} of {(data?.vendors || []).length} vendors shown · everything reads live from the register{qCols.length ? ` · ${qCols.length} question column${qCols.length === 1 ? '' : 's'}` : ''}</span>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50">Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
